/**
 * Task #2015 — Mappa canonica `service_key → capability richiesta`.
 *
 * Single source of truth condivisa BE/FE:
 *   - il recommender filtra il catalog tramite questa mappa quando deve
 *     produrre suggerimenti per un endpoint Admin AI Routing;
 *   - il FE (ServiceProviderSelect) la usa per sapere se un provider
 *     (incluso un custom endpoint) può servire un dato service_key;
 *   - il gate `check-custom-llm-capability-coverage` verifica che ogni
 *     capability sondabile sia presente in `runCapabilityProbeOnClient`.
 *
 * Le chiavi corrispondono 1:1 a `platform_model_config.service_key` (cfr.
 * `packages/backend/src/services/ai/platform-model-config.service.ts`).
 */

export const SERVICE_CAPABILITY_KEYS = [
  'chat',
  'embedding',
  'tts',
  'stt',
  'vision',
  'multimodal',
] as const;

export type ServiceCapability = (typeof SERVICE_CAPABILITY_KEYS)[number];

export interface ServiceDescriptor {
  service_key: string;
  capability: ServiceCapability;
  /** Etichetta UI breve. */
  label_it: string;
  /**
   * Il tenant può scegliere provider/modello per questo service_key: la riga
   * del tenant in `tenant_service_routing` vince sul default di piattaforma
   * (`platform_model_config`, gestito solo dal master). Falso solo per
   * `embedding`: il modello di embedding è deciso dal lock dell'embedder.
   */
  tenant_routable: boolean;
  /**
   * Falso quando il runtime del servizio non sa chiamare un endpoint Custom AI
   * (`custom:<uuid>`): la pipeline RAG (sintesi, scomposizione, riformulazione,
   * rerank, contesto dei frammenti), workflow e commissioning builder (la
   * factory li riceve senza riferimenti custom e li manda a OpenAI) e il
   * transcript realtime (Realtime API OpenAI) usano solo i provider nativi.
   * Assente = sì.
   */
  custom_routable?: false;
}

/**
 * Tabella esaustiva dei service_key governabili dall'Admin AI Routing.
 * Aggiungere QUI ogni nuovo service_key che richiede un provider modificabile.
 */
export const SERVICE_CAPABILITY_MAP: ReadonlyArray<ServiceDescriptor> = [
  // Embedding
  { service_key: 'embedding',              capability: 'embedding', label_it: 'Embedding (text → vector)',   tenant_routable: false },

  // RAG
  { service_key: 'rag_synthesis',          capability: 'chat',     label_it: 'RAG sintesi inferenziale',     tenant_routable: true, custom_routable: false },
  // `rag_synthesis_multimodal` è chat-based con filtro modality={text,image} +
  // required_features={vision}: il catalog non popola `capability='vision'`,
  // i modelli vision-capable sono chat con modality includes 'image'.
  { service_key: 'rag_synthesis_multimodal', capability: 'chat',     label_it: 'RAG sintesi multimodale',      tenant_routable: true, custom_routable: false },
  // Fallback dichiarativo tenant-scoped per `rag_synthesis_multimodal`. Quando
  // settato (provider ≠ 'none') restringe la chain L1/L2 a [primario, fallback]
  // e disabilita il cross-provider opportunistico. Opzionale: se assente, resta
  // la chain auto (L1 → L2 cross-provider → L3 text-only degrade).
  { service_key: 'rag_synthesis_multimodal_fallback', capability: 'chat', label_it: 'RAG sintesi multimodale (fallback)', tenant_routable: true, custom_routable: false },
  { service_key: 'rag_decomposition',      capability: 'chat',     label_it: 'RAG query decomposition',      tenant_routable: true, custom_routable: false },
  // `rag_rerank` è chat-based (i 4 reranker LLM in services/rerank/providers/*
  // chiamano un modello chat per riordinare i chunk). Marcato `chat` così tutti
  // i provider canonical (openai/claude/google) compaiono in
  // available-providers; nessun modello in catalog è marcato `rerank`.
  { service_key: 'rag_rerank',             capability: 'chat',     label_it: 'RAG rerank',                   tenant_routable: true, custom_routable: false },
  // Task #2097 — Secondario del cross-check rerank (RRF a≠b). Tenant-scoped:
  // ogni tenant attiva opzionalmente un secondo ranker indipendente dal
  // primario; provider+modello selezionabili a coppia coerente. Default
  // provider='none' = nessun secondario configurato (cross-check OFF).
  { service_key: 'rag_rerank_secondary',   capability: 'chat',     label_it: 'RAG rerank — Secondario (cross-check)', tenant_routable: true, custom_routable: false },
  { service_key: 'rag_eval',               capability: 'chat',     label_it: 'RAG evaluation',               tenant_routable: true },
  { service_key: 'rag_query_expansion_model', capability: 'chat',  label_it: 'RAG query expansion / HyDE',   tenant_routable: true, custom_routable: false },
  { service_key: 'rag_query_expansion',    capability: 'chat',     label_it: 'RAG Query Expansion (config)', tenant_routable: true },
  { service_key: 'rag_chunk_context',      capability: 'chat',     label_it: 'RAG contextual chunking',      tenant_routable: true, custom_routable: false },
  { service_key: 'rag_topk_strategy',      capability: 'chat',     label_it: 'RAG Adaptive Top-K (config)',  tenant_routable: true },
  { service_key: 'rag_quality_features',   capability: 'chat',     label_it: 'RAG Quality Feature Flags',    tenant_routable: true },

  // Content / files
  { service_key: 'workflow_builder',       capability: 'chat',     label_it: 'Workflow Builder AI',          tenant_routable: true, custom_routable: false },
  // Task #3414 — agente Incentivazione (commissioning builder).
  { service_key: 'commissioning_builder',  capability: 'chat',     label_it: 'Agente AI Incentivazione',     tenant_routable: true, custom_routable: false },
  { service_key: 'agent_chat',             capability: 'chat',     label_it: 'Agenti AI (modello predefinito)', tenant_routable: true },
  { service_key: 'conversation_analysis',  capability: 'chat',     label_it: 'Analisi conversazioni',        tenant_routable: true },
  { service_key: 'file_analysis',          capability: 'chat',     label_it: 'File analysis (vision)',       tenant_routable: true },
  { service_key: 'file_summary',           capability: 'chat',     label_it: 'File summary',                 tenant_routable: true },
  { service_key: 'landing_fallback',       capability: 'chat',     label_it: 'Landing Builder fallback',     tenant_routable: true },
  { service_key: 'landing_vision',         capability: 'chat',     label_it: 'Landing Builder vision',       tenant_routable: true },
  // `landing_image` è la generazione immagini del Site Builder. Task #2738:
  // capability='chat' + required_features=['image_generation'] (overlay FEATURE,
  // mai un valore Capability DB). Il recommender HA una riga weights per questo
  // service_key (vedi migration `ai_model_recommendation_weights`), così
  // `/available-providers` e il picker `TwoStepModelPicker capability="image"`
  // elencano i modelli image-output di OGNI provider attivo (OpenAI + Google).
  // Per-tenant: il modello immagini è scelto dal modale Site
  // Builder (PUT /ai-tools/service-models/landing_image) e risolto da
  // getPlatformModel('landing_image', { tenantId }) → override TSR per-tenant.
  { service_key: 'landing_image',          capability: 'chat',     label_it: 'Landing Builder image generation', tenant_routable: true },

  // OCR/parsing tenant-scoped
  // OCR/document_parsing: capability='chat' con required_modality={text,image} +
  // required_features={vision[,json_mode]}. Vedi commento `rag_synthesis_multimodal`.
  { service_key: 'ocr',                    capability: 'chat',     label_it: 'OCR PDF/immagini',             tenant_routable: true },
  { service_key: 'document_parsing',       capability: 'chat',     label_it: 'Document parsing',             tenant_routable: true },

  // Speech
  { service_key: 'tts_ivr',                capability: 'tts',      label_it: 'TTS IVR',                      tenant_routable: true },
  { service_key: 'stt_voicemail',          capability: 'stt',      label_it: 'STT voicemail',                tenant_routable: true },
  { service_key: 'stt_video',              capability: 'stt',      label_it: 'STT video transcription',      tenant_routable: true },
  { service_key: 'stt_realtime',           capability: 'stt',      label_it: 'STT realtime agents',          tenant_routable: true, custom_routable: false },
  { service_key: 'stt_workspace',          capability: 'stt',      label_it: 'STT workspace PA',             tenant_routable: true },
];

export function getServiceDescriptor(serviceKey: string): ServiceDescriptor | undefined {
  return SERVICE_CAPABILITY_MAP.find((s) => s.service_key === serviceKey);
}

export function getServiceCapability(serviceKey: string): ServiceCapability | undefined {
  return getServiceDescriptor(serviceKey)?.capability;
}

/** Vero se il tenant può instradare il service_key su un proprio provider/modello. */
export function isTenantRoutableService(serviceKey: string): boolean {
  return getServiceDescriptor(serviceKey)?.tenant_routable === true;
}

/** Vero se il runtime del service_key può eseguire su un endpoint Custom AI. */
export function isCustomRoutableService(serviceKey: string): boolean {
  const d = getServiceDescriptor(serviceKey);
  return !!d && d.custom_routable !== false;
}

/**
 * Capability per cui esiste una probe attiva in `runCapabilityProbeOnClient`.
 * Usato dal CI gate `check-custom-llm-capability-coverage`.
 */
export const PROBEABLE_SERVICE_CAPABILITIES: ReadonlyArray<ServiceCapability> = [
  'chat',
  'vision',
  'embedding',
  'stt',
  'tts',
];
